import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Divider
} from '@mui/material';
import { AppDispatch, RootState } from 'app/store';
import FeedbackAndComplaintsForm from 'components/NDIS_section/reportAllForms/forms/FeedbackComplaintsForm';
import MaintenanceRequestForm from 'components/NDIS_section/reportAllForms/forms/MaintenanceRequestForm';
import OutreachFormComponent from 'components/NDIS_section/reportAllForms/forms/OutreachForm';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  FaCalendarAlt,
  FaCommentAlt,
  FaEye,
  FaPeopleCarry,
  FaSearch,
  FaTasks,
  FaUser,
  FaWrench
} from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { fetchParticipantsList } from 'slices/HomeTilesSlices';
import {
  clearFeedbackState,
  FeedbackComplaint,
  fetchFeedbacks,
  MaintenanceRequest,
  OutreachForm
} from 'slices/NdisSlices/carporateServices/feedbackAndManagementListSlice';

interface FilterFormData {
  client_email: string;
  search: string;
}

type TabValue = 'feedback' | 'maintenance' | 'outreach';

const FeedbackAndManagementList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const dispatch = useDispatch<AppDispatch>();
  const { pathname } = useLocation();

  const [activeTab, setActiveTab] = useState<TabValue>('feedback');
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openFollowupModal, setOpenFollowupModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<
    FeedbackComplaint | MaintenanceRequest | OutreachForm | null
  >(null);
  const [isClientsLoading, setIsClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState('');

  // Get user details and project/service selections
  const loginInfo = localStorage.getItem('user-details');
  const userDetails = loginInfo ? JSON.parse(loginInfo) : null;
  const selectedProjectId = useSelector(
    (state: RootState) => state.homeTilesServices.selectedProjectId
  );
  const selectedServiceTitle = useSelector(
    (state: RootState) => state.serviceSelected.selectedServiceTitle
  );
  const email = userDetails?.email || '';
  const role = userDetails?.role ? [userDetails.role] : [''];

  // Fetch clients
  useEffect(() => {
    if (selectedProjectId && selectedServiceTitle && email && role.length > 0) {
      setIsClientsLoading(true);
      dispatch(
        fetchParticipantsList({
          group_id: selectedProjectId || '',
          service_name_id: selectedServiceTitle || '',
          nested_component_id: pathname,
          email: isAdminLogin ? '' : email,
          roles: role,
          client_name: ''
        })
      )
        .unwrap()
        .then(() => {
          setIsClientsLoading(false);
        })
        .catch((error) => {
          setIsClientsLoading(false);
          setClientsError('Failed to load clients. Please try again later.');
        });
    } else {
      setIsClientsLoading(false);
    }
  }, [selectedProjectId, selectedServiceTitle]);

  // Get feedback data
  const { feedback_complaints, maintenance_requests, outreach_forms, loading, error } = useSelector(
    (state: RootState) => state.feedbackAndManagementList
  );

  const participantsList = useSelector(
    (state: RootState) => state?.homeTilesServices?.participants
  ) || { participants_list: [] };

  // Prepare clients list
  const clientsList = useMemo(
    () =>
      participantsList.participants_list?.map(
        (participant: { name: string; email: string }, index: number) => ({
          label: participant.name || `Client ${index + 1}`,
          id: index + 1,
          email: participant.email
        })
      ) || [],
    [participantsList.participants_list]
  );

  // Get default client from localStorage
  const selectedClient = JSON.parse(localStorage.getItem('selectedClient') || '{}') as {
    email?: string;
  };
  const defaultClientEmail = selectedClient.email || '';

  // Form handling
  const { control, watch } = useForm<FilterFormData>({
    defaultValues: { client_email: defaultClientEmail, search: '' }
  });

  const clientEmail = watch('client_email');

  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
    projectId?: string;
    projectName?: string;
    isAdmin?: boolean;
  };

  const isAdminLogin =
    selectedProject?.isAdmin === true ||
    userDetails?.role?.some((r: any) => r?.label?.toLowerCase() === 'admin');


  // Fetch feedback data when client changes
  useEffect(() => {
    if (clientEmail || defaultClientEmail) {
      dispatch(fetchFeedbacks({ client_email: clientEmail || defaultClientEmail }));
    }
    return () => {
      dispatch(clearFeedbackState());
    };
  }, [dispatch, clientEmail, defaultClientEmail]);

  // Filter data function
  const filterData = <T extends { [key: string]: any }>(data: T[], fields: string[]): T[] => {
    const searchTerm = watch('search');
    if (!searchTerm) return data;
    const lowerSearch = searchTerm.toLowerCase();
    return data.filter((item) =>
      fields.some((field) => item[field]?.toString().toLowerCase().includes(lowerSearch))
    );
  };

  // Filtered data sets
  const filteredFeedbacks = filterData<FeedbackComplaint>(feedback_complaints, [
    'feedback_provider_name',
    'type_of_feedback',
    'description'
  ]);

  const filteredMaintenanceRequests = filterData<MaintenanceRequest>(maintenance_requests, [
    'name',
    'description',
    'location_of_issue',
    'urgency_level'
  ]);

  const filteredOutreachForms = filterData<OutreachForm>(outreach_forms, [
    'client_name',
    'type_of_outreach',
    'description'
  ]);

  // View and Followup handlers
  const handleView = (item: FeedbackComplaint | MaintenanceRequest | OutreachForm) => {
    setSelectedItem(item);
    setOpenViewModal(true);
  };

  const handleFollowup = (item: FeedbackComplaint | MaintenanceRequest | OutreachForm) => {
    setSelectedItem(item);
    setOpenFollowupModal(true);
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setSelectedItem(null);
  };

  const handleCloseFollowupModal = () => {
    setOpenFollowupModal(false);
    setSelectedItem(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
    setSelectedItem(null);
  };

  // Detail view renderer with improved label-value format
  const renderDetails = (item: FeedbackComplaint | MaintenanceRequest | OutreachForm) => {
    const formatLabel = (label: string) => {
      return label
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const formatValue = (value: any) => {
      if (value === null || value === undefined) return '—';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      return value.toString();
    };

    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {activeTab === 'feedback'
              ? 'Feedback Details'
              : activeTab === 'maintenance'
              ? 'Maintenance Details'
              : 'Outreach Details'}
          </Typography>
          <IconButton onClick={handleCloseViewModal} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {Object.entries(item).map(([key, value]) => (
            <React.Fragment key={key}>
              <Grid item xs={12}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {formatLabel(key)}
                  </Typography>
                  {key === 'photo' &&
                  typeof value === 'string' &&
                  value.startsWith('data:image') ? (
                    <Box
                      component="img"
                      src={value}
                      alt="Uploaded"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 150,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        objectFit: 'contain',
                        mt: 1
                      }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {formatValue(value)}
                    </Typography>
                  )}
                </Box>
                <Divider sx={{ my: 1 }} />
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Box>
    );
  };

  // Card renderer
  const renderCard = (item: FeedbackComplaint | MaintenanceRequest | OutreachForm) => {
    const getBorderColor = () => {
      switch (activeTab) {
        case 'feedback':
          return theme.palette.warning.light;
        case 'maintenance':
          return theme.palette.error.light;
        case 'outreach':
          return theme.palette.success.light;
        default:
          return theme.palette.primary.light;
      }
    };

    const getCardContent = () => {
      switch (activeTab) {
        case 'feedback':
          const feedback = item as FeedbackComplaint;
          return (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {feedback.feedback_provider_name || 'Anonymous'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {feedback.type_of_feedback}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: '0.875rem'
                }}
              >
                {feedback.description}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <FaCalendarAlt style={{ marginRight: 4, fontSize: '0.75rem' }} />
                  {feedback.date_of_feedback}
                </Typography>
                <Typography
                  variant="caption"
                  color={feedback.resolved ? 'success.main' : 'text.secondary'}
                >
                  {feedback.resolved ? 'Resolved' : 'Pending'}
                </Typography>
              </Box>
            </>
          );

        case 'maintenance':
          const request = item as MaintenanceRequest;
          return (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {request.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {request.role}
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" display="block">
                  <strong>Location:</strong> {request.location_of_issue}
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Urgency:</strong> {request.urgency_level}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: '0.875rem'
                }}
              >
                {request.description}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <FaCalendarAlt style={{ marginRight: 4, fontSize: '0.75rem' }} />
                {request.date_of_request}
              </Typography>
            </>
          );

        case 'outreach':
          const form = item as OutreachForm;
          return (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {form.client_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {form.type_of_outreach}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: '0.875rem'
                }}
              >
                {form.description}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <FaCalendarAlt style={{ marginRight: 4, fontSize: '0.75rem' }} />
                  {form.date_of_outreach}
                </Typography>
                <Typography
                  variant="caption"
                  color={form.successful ? 'success.main' : 'text.secondary'}
                >
                  {form.successful ? 'Successful' : 'Pending'}
                </Typography>
              </Box>
            </>
          );

        default:
          return null;
      }
    };

    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 1,
          borderLeft: `4px solid ${getBorderColor()}`,
          '&:hover': {
            boxShadow: 3
          },
          transition: 'all 0.2s ease'
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: 2 }}>{getCardContent()}</CardContent>
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            size="small"
            onClick={() => handleFollowup(item)}
            startIcon={<FaTasks size={14} />}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              p: '4px 8px'
            }}
          >
            Followup
          </Button>
          <Button
            size="small"
            onClick={() => handleView(item)}
            startIcon={<FaEye size={14} />}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              p: '4px 8px'
            }}
          >
            Details
          </Button>
        </Box>
      </Card>
    );
  };

  // Form renderer for followup modal
  const renderFollowupForm = () => {
    if (!selectedItem) return null;

    switch (activeTab) {
      case 'feedback':
        return (
          <FeedbackAndComplaintsForm
            initialData={selectedItem as FeedbackComplaint}
            onClose={handleCloseFollowupModal}
          />
        );
      case 'maintenance':
        return (
          <MaintenanceRequestForm
            initialData={selectedItem as MaintenanceRequest}
            onClose={handleCloseFollowupModal}
          />
        );
      case 'outreach':
        return <OutreachFormComponent initialData={selectedItem as OutreachForm} />;
      default:
        return null;
    }
  };

  // Tab content renderer
  const renderTabContent = () => {
    const items =
      activeTab === 'feedback'
        ? filteredFeedbacks
        : activeTab === 'maintenance'
        ? filteredMaintenanceRequests
        : filteredOutreachForms;

    return (
      <Box sx={{ flex: 1 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {activeTab === 'feedback'
              ? 'Feedback & Complaints'
              : activeTab === 'maintenance'
              ? 'Maintenance Requests'
              : 'Outreach Forms'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {items.length} {items.length === 1 ? 'record' : 'records'}
          </Typography>
        </Box>

        {items.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2">No records found for the selected filters</Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {items.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                {renderCard(item)}
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2, px: { xs: 1, sm: 2 }, pb: isMobile ? 8 : 2 }}>
      {/* Filters */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          gap: 2,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center'
        }}
      >
        <Controller
          name="client_email"
          control={control}
          render={({ field }) => (
            <Select
              size="small"
              value={field.value}
              onChange={(e: SelectChangeEvent<string>) => field.onChange(e.target.value)}
              displayEmpty
              fullWidth={isMobile}
              sx={{ minWidth: isMobile ? '100%' : 250 }}
              inputProps={{ 'aria-label': 'Select client' }}
              startAdornment={
                <InputAdornment position="start">
                  <FaUser size={14} />
                </InputAdornment>
              }
              disabled={isClientsLoading}
            >
              <MenuItem value="">All Clients</MenuItem>
              {clientsList.map((client) => (
                <MenuItem key={client.id} value={client.email}>
                  {client.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
        <Controller
          name="search"
          control={control}
          render={({ field }) => (
            <TextField
              size="small"
              placeholder="Search..."
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch size={14} />
                  </InputAdornment>
                )
              }}
              {...field}
            />
          )}
        />
      </Box>

      {/* Error Messages */}
      {clientsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setClientsError('')}>
          {clientsError}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearFeedbackState())}>
          {error}
        </Alert>
      )}

      {/* Loading States */}
      {(isClientsLoading || loading) && (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}
        >
          <CircularProgress size={isMobile ? 24 : 40} />
        </Box>
      )}

      {/* Main Content */}
      {!(isClientsLoading || loading) && (
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', gap: 2 }}>
          {/* Desktop Tabs */}
          {!isMobile && (
            <Paper
              sx={{
                width: 200,
                p: 1.5,
                height: 'fit-content',
                position: 'sticky',
                top: 80,
                borderRadius: 1.5
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                Categories
              </Typography>
              <Tabs
                orientation="vertical"
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    py: 1,
                    px: 1.5,
                    minHeight: 'auto'
                  },
                  '& .Mui-selected': {
                    bgcolor: theme.palette.action.selected,
                    color: theme.palette.primary.main,
                    borderRadius: 1
                  }
                }}
              >
                <Tab
                  label="Feedback & Complaints"
                  value="feedback"
                  icon={<FaCommentAlt size={14} style={{ marginRight: 8 }} />}
                  iconPosition="start"
                />
                <Tab
                  label="Maintenance Requests"
                  value="maintenance"
                  icon={<FaWrench size={14} style={{ marginRight: 8 }} />}
                  iconPosition="start"
                />
                <Tab
                  label="Outreach Forms"
                  value="outreach"
                  icon={<FaPeopleCarry size={14} style={{ marginRight: 8 }} />}
                  iconPosition="start"
                />
              </Tabs>
            </Paper>
          )}

          {/* Tab Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>{renderTabContent()}</Box>
        </Box>
      )}

      {/* Mobile Bottom Tabs */}
      {isMobile && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              width: '100%',
              '& .MuiTab-root': {
                minWidth: 'unset',
                minHeight: 56,
                fontSize: '0.75rem',
                px: 1.5
              }
            }}
          >
            <Tab
              label="Feedback"
              value="feedback"
              icon={<FaCommentAlt size={16} />}
              iconPosition="top"
            />
            <Tab
              label="Maintenance"
              value="maintenance"
              icon={<FaWrench size={16} />}
              iconPosition="top"
            />
            <Tab
              label="Outreach"
              value="outreach"
              icon={<FaPeopleCarry size={16} />}
              iconPosition="top"
            />
          </Tabs>
        </Paper>
      )}

      {/* View Modal */}
      <Modal
        open={openViewModal}
        onClose={handleCloseViewModal}
        aria-labelledby="view-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2 }
        }}
      >
        <Paper
          sx={{
            width: '100%',
            maxWidth: { xs: '95%', sm: 800 },
            maxHeight: '90vh',
            overflowY: 'auto',
            borderRadius: 1.5,
            position: 'relative'
          }}
        >
          {selectedItem && renderDetails(selectedItem)}
        </Paper>
      </Modal>

      {/* Followup Modal */}
      <Modal
        open={openFollowupModal}
        onClose={handleCloseFollowupModal}
        aria-labelledby="followup-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2 }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: { xs: '95%', sm: 800 },
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 1.5,
            boxShadow: 24
          }}
        >
          <IconButton
            onClick={handleCloseFollowupModal}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              bgcolor: 'background.default',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {renderFollowupForm()}
        </Box>
      </Modal>
    </Container>
  );
};

export default FeedbackAndManagementList;
